<template>
    <div class="animated fadeIn master-parameter-page">
        <LoadingWidget v-if="initStatus != 1" :status="initStatus" @init="init" />
        <div v-else class="master-form-card">
            <h5 class="master-page-title mb-3">Settings/ Parameter</h5>

            <!-- Wilayah Selector Box (Per-Wilayah Scoping) -->
            <div class="wilayah-selector-box mb-4">
                <div class="wilayah-label-mono mb-2">BERLAKU UNTUK WILAYAH</div>
                <div class="d-flex flex-wrap align-items-center gap-10">
                    <div class="wilayah-select-wrapper">
                        <b-form-select 
                            v-model="selectedWilayahId" 
                            :options="optWilayah" 
                            value-field="id" 
                            text-field="name" 
                            class="form-control-custom select-wilayah"
                            @change="onWilayahChange"
                            :disabled="isLoadingWilayah"
                        ></b-form-select>
                    </div>
                    <button type="button" class="btn btn-copy-wilayah ml-lg-2" @click="openModalCopy" :disabled="optWilayah.length <= 1">
                        <i class="fa fa-clone mr-1"></i> COPY TO
                    </button>
                    <span class="wilayah-desc-text ml-lg-2">
                        Nilai di seluruh tab di bawah hanya berlaku untuk <strong>{{ currentWilayahName }}</strong> · {{ currentWilayahSummary }}.
                    </span>
                </div>
            </div>

            <!-- Tab Navigation & Content -->
            <b-tabs v-model="tabIndex" class="custom-parameter-tabs" content-class="parameter-tab-body mt-3">
                <b-tab title="BUNGA & GADAI">
                    <BungaGadai 
                        :bungaGadaiInput="input.bungaGadai"
                        :jenisPinjamanList="jenisPinjamanParameters"
                        :wilayahId="selectedWilayahId"
                        @refresh="fetchWilayahParameter"
                    />
                </b-tab>
                <b-tab title="BIAYA ADMIN">
                    <BiayaAdmin 
                        :biayaAdminInput="input.biayaAdmin"
                        :jenisPinjamanList="jenisPinjamanParameters"
                        :barangJenisList="barangJenisParameters"
                        :wilayahId="selectedWilayahId"
                        @refresh="fetchWilayahParameter"
                    />
                </b-tab>
                <b-tab title="DENDA & PENALTI">
                    <DendaPenalti 
                        :dendaPenaltiInput="input.dendaPenalti"
                        :jenisPinjamanList="jenisPinjamanParameters"
                        :barangJenisList="barangJenisParameters"
                        :wilayahId="selectedWilayahId"
                        @refresh="fetchWilayahParameter"
                    />
                </b-tab>
                <b-tab title="ANGGOTA">
                    <Anggota 
                        :anggotaInput="input.anggota"
                        :jenisPinjamanList="jenisPinjamanParameters"
                        :wilayahId="selectedWilayahId"
                        @refresh="fetchWilayahParameter"
                    />
                </b-tab>
                <b-tab title="ADMINISTRASI">
                    <Administrasi :administrasiInput="input.administrasi" />
                </b-tab>
            </b-tabs>
        </div>

        <!-- Modal Copy To Wilayah -->
        <b-modal id="modal-copy-wilayah" title="Copy Parameter ke Wilayah Lain" centered hide-footer>
            <div class="p-2">
                <p class="small text-muted mb-3">
                    Salin seluruh konfigurasi Bunga, Biaya Admin, Denda, dan Penalti dari <strong>{{ currentWilayahName }}</strong> ke wilayah tujuan di bawah ini.
                </p>
                <b-form-group label="Wilayah Tujuan">
                    <b-form-select 
                        v-model="targetCopyWilayahId" 
                        :options="optTargetWilayah" 
                        value-field="id" 
                        text-field="name" 
                        class="form-control-custom"
                    >
                        <template v-slot:first>
                            <b-form-select-option :value="null" disabled>-- Pilih Wilayah Tujuan --</b-form-select-option>
                        </template>
                    </b-form-select>
                </b-form-group>
                <div class="d-flex justify-content-end gap-2 mt-4">
                    <b-button variant="secondary" class="mr-2" @click="$bvModal.hide('modal-copy-wilayah')">Batal</b-button>
                    <b-button variant="success" style="background-color: #0b3b36; border-color: #0b3b36;" :disabled="!targetCopyWilayahId || isCopying" @click="submitCopyWilayah">
                        <b-spinner small v-if="isCopying" class="mr-1"></b-spinner>
                        {{ isCopying ? 'Menyalin...' : 'Salin Parameter' }}
                    </b-button>
                </div>
            </div>
        </b-modal>
    </div>
</template>

<script>
import BungaGadai from '../../../components/parameter/BungaGadai';
import BiayaAdmin from '../../../components/parameter/BiayaAdmin';
import DendaPenalti from '../../../components/parameter/DendaPenalti';
import Anggota from '../../../components/parameter/Anggota';
import Administrasi from '../../../components/parameter/Administrasi';

export default {
    name: 'master-parameter',
    components: {
        BungaGadai,
        BiayaAdmin,
        DendaPenalti,
        Anggota,
        Administrasi
    },
    data() {
        return {
            initStatus: 0,
            tabIndex: 0,
            selectedWilayahId: null,
            targetCopyWilayahId: null,
            optWilayah: [],
            isLoadingWilayah: false,
            isCopying: false,
            jenisPinjamanParameters: [],
            barangJenisParameters: [],
            masterJenisPinjaman: [],
            input: {
                bungaGadai: {
                    BONUS_BUNGA_DIDEPAN: null,
                    HARGA_TAKSIRAN: null,
                    PINJAMAN_MINIMAL: null,
                    BATAS_BAWAH_GADAI: null,
                    NOTIF_KU_MIN: null,
                    NOTIF_KC_MIN: null,
                    NOTIF_KCS_MIN: null,
                    PERSENTASE_NILAI_X: null
                },
                biayaAdmin: {},
                dendaPenalti: {
                    GRACE_PERIOD: null
                },
                anggota: {
                    MAX_GADAI_JENIS: null,
                    MAX_PERPANJANGAN: null,
                    BONUS_GADAI_PERTAMA: null,
                    ADJUST_MACET: null,
                    ADJUST_KURANG_LANCAR: null,
                    ADJUST_LANCAR: null,
                    ADJUST_BARU: null,
                    HARI_MACET: null,
                    HARI_KURANG_LANCAR: null
                },
                administrasi: {
                    EKS_1_BARANG_HILANG: null,
                    EKS_2_BARANG_HILANG: null,
                    NOTIF_KIRIM_BALIK: null
                }
            }
        }
    },
    computed: {
        currentWilayah() {
            return this.optWilayah.find(w => w.id === this.selectedWilayahId) || null
        },
        currentWilayahName() {
            return this.currentWilayah ? this.currentWilayah.name : 'Wilayah Default'
        },
        currentWilayahSummary() {
            return this.currentWilayah && this.currentWilayah.areaSummary ? this.currentWilayah.areaSummary : 'mencakup seluruh area operasional'
        },
        optTargetWilayah() {
            return this.optWilayah.filter(w => w.id !== this.selectedWilayahId)
        }
    },
    methods: {
        async init() {
            this.initStatus = 0
            try {
                await this.fetchMasterJenisPinjaman()
                await this.fetchWilayahList()
                if (this.selectedWilayahId) {
                    await this.fetchWilayahParameter()
                } else {
                    await this.fetchGlobalFallbackParameters()
                }
                this.initStatus = 1
            } catch (err) {
                console.error("Gagal inisialisasi Master Parameter:", err)
                await this.fetchGlobalFallbackParameters()
                this.initStatus = 1
            }
        },

        async fetchMasterJenisPinjaman() {
            try {
                const res = await this.$axios.get("jenis-pinjaman/")
                let list = Array.isArray(res.data) ? res.data : (res.data && Array.isArray(res.data.data) ? res.data.data : [])
                this.masterJenisPinjaman = list.filter(j => j.isActive !== false && j.is_active !== false)
            } catch (e) {
                console.warn("Gagal load master jenis-pinjaman:", e)
                this.masterJenisPinjaman = [
                    { id: 1, name: "Elektronik", code: "ELK" },
                    { id: 2, name: "Kendaraan", code: "KND" },
                    { id: 3, name: "BPKB", code: "BPK" },
                    { id: 4, name: "Emas", code: "EMS" }
                ]
            }
        },

        async fetchWilayahList() {
            this.isLoadingWilayah = true
            try {
                const res = await this.$axios.get("parameter/wilayah-list")
                let list = Array.isArray(res.data) ? res.data : []
                if (list.length === 0) {
                    list = [{ id: 1, name: "Default", isDefault: true, areaSummary: "seluruh area nasional" }]
                }
                this.optWilayah = list
                
                const defaultW = list.find(w => w.isDefault) || list[0]
                if (defaultW) {
                    this.selectedWilayahId = defaultW.id
                }
            } catch (error) {
                console.warn("Gagal load wilayah-list:", error)
                this.optWilayah = [{ id: 1, name: "Default", isDefault: true, areaSummary: "seluruh area nasional" }]
                this.selectedWilayahId = 1
            } finally {
                this.isLoadingWilayah = false
            }
        },

        async fetchWilayahParameter() {
            if (!this.selectedWilayahId) return
            try {
                const res = await this.$axios.get(`parameter/wilayah/${this.selectedWilayahId}`)
                const data = res.data || {}

                let jpList = Array.isArray(data.jenisPinjamanParameters) ? data.jenisPinjamanParameters : []
                
                // Merge masterJenisPinjaman with fetched data so all active loan types are always present
                if (this.masterJenisPinjaman && this.masterJenisPinjaman.length > 0) {
                    const merged = this.masterJenisPinjaman.map(mjp => {
                        const existing = jpList.find(p => p.jenisPinjamanId === mjp.id)
                        if (existing) {
                            return {
                                ...existing,
                                jenisPinjamanName: mjp.name || existing.jenisPinjamanName,
                                jenisPinjamanKode: mjp.code || mjp.kode || existing.jenisPinjamanKode || ''
                            }
                        }
                        return {
                            id: null,
                            wilayahId: this.selectedWilayahId,
                            jenisPinjamanId: mjp.id,
                            jenisPinjamanName: mjp.name,
                            jenisPinjamanKode: mjp.code || mjp.kode || '',
                            bungaTenor1: 0,
                            bungaTenor2: 0,
                            admAwalPersen: 0,
                            admAwalNominalMin: 0,
                            admBulananPersen: 0,
                            admBulananNominalMin: 0,
                            dendaHarianPersen: 0,
                            maxGadaiNominal: 0
                        }
                    })
                    jpList = merged
                }

                this.jenisPinjamanParameters = jpList
                this.barangJenisParameters = Array.isArray(data.barangJenisParameters) ? data.barangJenisParameters : []

                const g = data.globalParameters || {}
                this.populateGlobalParameters(g)
            } catch (err) {
                console.warn("Gagal load detail parameter wilayah, fallback ke global parameter:", err)
                await this.fetchGlobalFallbackParameters()
            }
        },

        populateGlobalParameters(g) {
            // 1. Bunga & Gadai Global
            this.input.bungaGadai.BONUS_BUNGA_DIDEPAN = g.BONUS_BUNGA_DIDEPAN !== undefined && g.BONUS_BUNGA_DIDEPAN !== null ? g.BONUS_BUNGA_DIDEPAN : 0
            this.input.bungaGadai.HARGA_TAKSIRAN = g.HARGA_TAKSIRAN !== undefined && g.HARGA_TAKSIRAN !== null ? g.HARGA_TAKSIRAN : 120
            this.input.bungaGadai.PINJAMAN_MINIMAL = g.PINJAMAN_MINIMAL !== undefined && g.PINJAMAN_MINIMAL !== null ? g.PINJAMAN_MINIMAL : 50
            this.input.bungaGadai.BATAS_BAWAH_GADAI = g.BATAS_BAWAH_GADAI !== undefined && g.BATAS_BAWAH_GADAI !== null ? g.BATAS_BAWAH_GADAI : 30
            this.input.bungaGadai.NOTIF_KU_MIN = this.$helper.addThousandSeparator(g.NOTIF_KU_MIN || 1900000)
            this.input.bungaGadai.NOTIF_KC_MIN = this.$helper.addThousandSeparator(g.NOTIF_KC_MIN || 3000000)
            this.input.bungaGadai.NOTIF_KCS_MIN = this.$helper.addThousandSeparator(g.NOTIF_KCS_MIN || 4500000)
            this.input.bungaGadai.PERSENTASE_NILAI_X = g.PERSENTASE_NILAI_X !== undefined && g.PERSENTASE_NILAI_X !== null ? g.PERSENTASE_NILAI_X : 114.50

            // 2. Denda & Penalti Global
            this.input.dendaPenalti.GRACE_PERIOD = g.GRACE_PERIOD !== undefined && g.GRACE_PERIOD !== null ? g.GRACE_PERIOD : 3

            // 3. Anggota Global
            this.input.anggota.MAX_GADAI_JENIS = g.MAX_GADAI_JENIS !== undefined && g.MAX_GADAI_JENIS !== null ? g.MAX_GADAI_JENIS : 2
            this.input.anggota.MAX_PERPANJANGAN = g.MAX_PERPANJANGAN !== undefined && g.MAX_PERPANJANGAN !== null ? g.MAX_PERPANJANGAN : 4
            this.input.anggota.BONUS_GADAI_PERTAMA = this.$helper.addThousandSeparator(g.BONUS_GADAI_PERTAMA || 100000)
            this.input.anggota.ADJUST_MACET = g.ADJUST_MACET !== undefined && g.ADJUST_MACET !== null ? g.ADJUST_MACET : 60
            this.input.anggota.ADJUST_KURANG_LANCAR = g.ADJUST_KURANG_LANCAR !== undefined && g.ADJUST_KURANG_LANCAR !== null ? g.ADJUST_KURANG_LANCAR : 80
            this.input.anggota.ADJUST_LANCAR = g.ADJUST_LANCAR !== undefined && g.ADJUST_LANCAR !== null ? g.ADJUST_LANCAR : 110
            this.input.anggota.ADJUST_BARU = g.ADJUST_BARU !== undefined && g.ADJUST_BARU !== null ? g.ADJUST_BARU : 100
            this.input.anggota.HARI_MACET = g.HARI_MACET !== undefined && g.HARI_MACET !== null ? g.HARI_MACET : 15
            this.input.anggota.HARI_KURANG_LANCAR = g.HARI_KURANG_LANCAR !== undefined && g.HARI_KURANG_LANCAR !== null ? g.HARI_KURANG_LANCAR : 2

            // 4. Administrasi Global
            this.input.administrasi.EKS_1_BARANG_HILANG = g.EKS_1_BARANG_HILANG || 1
            this.input.administrasi.EKS_2_BARANG_HILANG = g.EKS_2_BARANG_HILANG || 1
            this.input.administrasi.NOTIF_KIRIM_BALIK = g.NOTIF_KIRIM_BALIK || 1
        },

        async fetchGlobalFallbackParameters() {
            if (this.masterJenisPinjaman && this.masterJenisPinjaman.length > 0) {
                this.jenisPinjamanParameters = this.masterJenisPinjaman.map(mjp => ({
                    id: null,
                    wilayahId: this.selectedWilayahId || 1,
                    jenisPinjamanId: mjp.id,
                    jenisPinjamanName: mjp.name,
                    jenisPinjamanKode: mjp.code || mjp.kode || '',
                    bungaTenor1: 0,
                    bungaTenor2: 0,
                    admAwalPersen: 0,
                    admAwalNominalMin: 0,
                    admBulananPersen: 0,
                    admBulananNominalMin: 0,
                    dendaHarianPersen: 0,
                    maxGadaiNominal: 0
                }))
            }

            try {
                const res = await this.$axios.get("parameter/")
                const g = res.data || {}
                this.populateGlobalParameters(g)
            } catch (e) {
                console.warn("Gagal load global parameter fallback:", e)
                this.populateGlobalParameters({})
            }
        },

        onWilayahChange() {
            this.fetchWilayahParameter()
        },

        openModalCopy() {
            this.targetCopyWilayahId = null
            this.$bvModal.show("modal-copy-wilayah")
        },

        async submitCopyWilayah() {
            if (!this.targetCopyWilayahId) return
            this.isCopying = true
            try {
                let payload = new FormData()
                payload.append("fromWilayahId", this.selectedWilayahId)
                payload.append("toWilayahId", this.targetCopyWilayahId)

                await this.$axios.post("parameter/copy-wilayah", payload)
                this.$helper.toastSucc(this, "Parameter berhasil disalin ke wilayah tujuan!")
                this.$bvModal.hide("modal-copy-wilayah")
            } catch (err) {
                this.$helper.parseError(this, err)
            } finally {
                this.isCopying = false
            }
        }
    },
    created() {
        this.init()
    }
}
</script>

<style lang="scss" scoped>
.master-parameter-page {
    padding-bottom: 40px;
}

.master-form-card {
    background: #ffffff;
    border-radius: 12px;
    padding: 24px 28px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.03);
    border: 1px solid #edf2f0;
}

.master-page-title {
    font-size: 20px;
    font-weight: 700;
    color: #0b3b36;
    letter-spacing: -0.2px;
}

/* Wilayah Selector Box */
.wilayah-selector-box {
    background-color: #fbfdfc;
    border: 1px solid #e2ece7;
    border-radius: 10px;
    padding: 16px 20px;
}

.wilayah-label-mono {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.8px;
    color: #64748b;
    font-family: inherit;
}

.select-wilayah {
    border-radius: 8px !important;
    border: 1px solid #c9dbd3 !important;
    font-size: 13.5px !important;
    font-weight: 600 !important;
    color: #0b3b36 !important;
    background-color: #ffffff !important;
    height: 38px !important;
    min-width: 220px;
}

.btn-copy-wilayah {
    background-color: #ffffff;
    color: #0b3b36;
    border: 1.5px solid #0b3b36;
    border-radius: 8px;
    font-size: 12.5px;
    font-weight: 700;
    letter-spacing: 0.4px;
    padding: 7px 16px;
    display: inline-flex;
    align-items: center;
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
        background-color: #0b3b36;
        color: #ffffff;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
}

.wilayah-desc-text {
    font-size: 12.5px;
    color: #64748b;
}

/* Custom Tabs Styling (Dark Green Pill Theme) */
::v-deep .custom-parameter-tabs {
    .nav-tabs {
        border-bottom: none;
        gap: 6px;
        background-color: #f1f6f4;
        padding: 5px;
        border-radius: 10px;
        display: inline-flex;
        margin-bottom: 20px;
    }

    .nav-link {
        border: none !important;
        border-radius: 8px !important;
        font-size: 12.5px;
        font-weight: 700;
        letter-spacing: 0.4px;
        color: #475569;
        padding: 8px 18px;
        background: transparent;
        transition: all 0.2s ease;

        &:hover {
            color: #0b3b36;
            background-color: rgba(11, 59, 54, 0.05);
        }

        &.active {
            background-color: #0b3b36 !important;
            color: #ffffff !important;
            box-shadow: 0 2px 6px rgba(11, 59, 54, 0.2);
        }
    }
}
</style>
